import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Search, FileText, ChevronDown, ChevronRight,
  Users, UserCheck, Calendar, Filter,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlatformLayout } from "./platform-layout";

interface AuditEntry {
  id: string;
  workspaceId: string;
  workspaceName?: string;
  entityId: string;
  action: string;
  actorUserId: string | null;
  actorEmail: string | null;
  actorType: "user" | "system" | "platform_admin";
  entitySnapshot: Record<string, any> | null;
  changedFields: Record<string, { old: any; new: any }> | null;
  metadata: Record<string, any> | null;
  createdAt: string;
}

const LEAD_ACTION_META: Record<string, { label: string; color: string }> = {
  lead_created:        { label: "Lead Created",        color: "bg-green-500" },
  lead_updated:        { label: "Lead Updated",        color: "bg-blue-500" },
  lead_status_changed: { label: "Status Changed",      color: "bg-amber-500" },
  lead_booked:         { label: "Lead Booked",         color: "bg-emerald-600" },
  lead_deleted:        { label: "Lead Deleted",        color: "bg-red-500" },
  lead_restored:       { label: "Lead Restored",       color: "bg-teal-500" },
};

const CLIENT_ACTION_META: Record<string, { label: string; color: string }> = {
  client_created:  { label: "Client Created",  color: "bg-green-500" },
  client_updated:  { label: "Client Updated",  color: "bg-blue-500" },
  client_deleted:  { label: "Client Deleted",  color: "bg-red-500" },
  client_restored: { label: "Client Restored", color: "bg-teal-500" },
};

function formatValue(v: any): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function DiffRow({ field, old: oldVal, new: newVal }: { field: string; old: any; new: any }) {
  return (
    <div className="grid grid-cols-[160px_1fr_1fr] gap-2 text-xs py-1 border-b border-border/40 last:border-0">
      <span className="font-mono text-muted-foreground truncate">{field}</span>
      <span className="text-red-500 truncate" title={formatValue(oldVal)}>{formatValue(oldVal)}</span>
      <span className="text-green-600 truncate" title={formatValue(newVal)}>{formatValue(newVal)}</span>
    </div>
  );
}

const LEAD_SNAPSHOT_FIELDS = [
  "leadName", "contactName", "emailAddress", "phoneNumber", "status",
  "clientType", "rep", "recordId", "talentId", "regionId",
];
const CLIENT_SNAPSHOT_FIELDS = [
  "accountName", "contactName", "emailAddress", "dateOfEvent", "eventTime",
  "status", "clientType", "rep", "recordId", "talentId",
];

function SnapshotBlock({ snapshot, entityType }: { snapshot: Record<string, any>; entityType: "lead" | "client" }) {
  const fields = entityType === "client" ? CLIENT_SNAPSHOT_FIELDS : LEAD_SNAPSHOT_FIELDS;
  const relevant = fields.filter((f) => f in snapshot);
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs mt-1">
      {relevant.map((f) => (
        <div key={f} className="flex gap-1 min-w-0">
          <span className="font-mono text-muted-foreground shrink-0">{f}:</span>
          <span className="truncate" title={formatValue(snapshot[f])}>{formatValue(snapshot[f])}</span>
        </div>
      ))}
    </div>
  );
}

function AuditRow({ entry, entityType }: { entry: AuditEntry; entityType: "lead" | "client" }) {
  const [open, setOpen] = useState(false);
  const actionMeta = entityType === "client" ? CLIENT_ACTION_META : LEAD_ACTION_META;
  const meta = actionMeta[entry.action] ?? { label: entry.action, color: "bg-slate-500" };
  const hasDiff = entry.changedFields && Object.keys(entry.changedFields).length > 0;
  const hasSnapshot = !!entry.entitySnapshot;
  const canExpand = hasDiff || hasSnapshot;

  const displayName = entry.metadata?.leadName ?? entry.metadata?.accountName ?? null;
  const recordId = entry.metadata?.recordId ?? null;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => canExpand && setOpen((o) => !o)}
        className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors ${canExpand ? "cursor-pointer" : "cursor-default"}`}
        data-testid={`lead-audit-row-${entry.id}`}
      >
        {canExpand ? (
          open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <span className="w-3.5" />
        )}

        <span className={`w-2 h-2 rounded-full shrink-0 ${meta.color}`} />

        <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto] gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-medium text-sm">{meta.label}</span>
            {recordId && (
              <span className="text-xs text-muted-foreground font-mono">{recordId}</span>
            )}
            {displayName && (
              <span className="text-xs text-muted-foreground truncate hidden md:block">· {displayName}</span>
            )}
            {entry.metadata?.status && (
              <Badge variant="outline" className="text-xs h-4 px-1">{entry.metadata.status}</Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
            <span className="hidden sm:block">
              {entry.workspaceName ?? entry.workspaceId.slice(0, 8)}
            </span>
            <span>·</span>
            <span>{entry.actorEmail ?? entry.actorType}</span>
            <span>·</span>
            <span>{new Date(entry.createdAt).toLocaleString()}</span>
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-border bg-muted/20 px-4 py-3 space-y-3 text-sm">
          {hasDiff && (
            <div>
              <div className="text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Changed Fields</div>
              <div className="grid grid-cols-[160px_1fr_1fr] gap-2 text-xs font-medium text-muted-foreground mb-1">
                <span>Field</span><span className="text-red-400">Before</span><span className="text-green-500">After</span>
              </div>
              {Object.entries(entry.changedFields!).map(([field, { old: o, new: n }]) => (
                <DiffRow key={field} field={field} old={o} new={n} />
              ))}
            </div>
          )}
          {hasSnapshot && (
            <div>
              <div className="text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">
                {hasDiff ? "Snapshot After Change" : "Full Snapshot"}
              </div>
              <SnapshotBlock snapshot={entry.entitySnapshot!} entityType={entityType} />
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Entity ID: <span className="font-mono">{entry.entityId}</span>
            {entry.actorUserId && (
              <> · Actor ID: <span className="font-mono">{entry.actorUserId}</span></>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AuditTab({ entityType }: { entityType: "lead" | "client" }) {
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const params = new URLSearchParams();
  params.set("entityType", entityType);
  if (search) params.set("search", search);
  if (action !== "all") params.set("action", action);
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String(page * PAGE_SIZE));

  const { data, isLoading } = useQuery<{ entries: AuditEntry[]; total: number }>({
    queryKey: ["/api/platform/lead-audit", entityType, search, action, startDate, endDate, page],
    queryFn: () => fetch(`/api/platform/lead-audit?${params}`, { credentials: "include" }).then((r) => r.json()),
  });

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;
  const actionMeta = entityType === "client" ? CLIENT_ACTION_META : LEAD_ACTION_META;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={entityType === "lead" ? "Lead name, record ID, actor email…" : "Account name, record ID, actor email…"}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-9"
                data-testid={`input-${entityType}-audit-search`}
              />
            </div>
            <Select value={action} onValueChange={(v) => { setAction(v); setPage(0); }}>
              <SelectTrigger className="w-44" data-testid={`select-${entityType}-action`}>
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {Object.entries(actionMeta).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              className="w-36"
              data-testid={`input-${entityType}-start-date`}
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              className="w-36"
              data-testid={`input-${entityType}-end-date`}
            />
            {(search || action !== "all" || startDate || endDate) && (
              <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setAction("all"); setStartDate(""); setEndDate(""); setPage(0); }}>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
        <span>{isLoading ? "Loading…" : `${total.toLocaleString()} entries`}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid={`button-${entityType}-prev-page`}>← Prev</Button>
          <span className="px-2 py-1.5 text-xs">Page {page + 1}</span>
          <Button variant="outline" size="sm" disabled={entries.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)} data-testid={`button-${entityType}-next-page`}>Next →</Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-14 bg-muted/40 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="font-medium text-muted-foreground">No entries found</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              {search || action !== "all"
                ? "Try broadening your filters."
                : `${entityType === "lead" ? "Lead" : "Client"} changes will appear here as they occur.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <AuditRow key={entry.id} entry={entry} entityType={entityType} />
          ))}
        </div>
      )}
    </div>
  );
}

export function LeadAuditContent() {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Lead & Client Audit Log
          </CardTitle>
          <CardDescription>
            Immutable ledger of every lead and client record change across all workspaces.
            Append-only — no entry can be edited or deleted.
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs defaultValue="leads">
        <TabsList>
          <TabsTrigger value="leads" data-testid="tab-leads-audit">
            <Users className="h-4 w-4 mr-1.5" /> Leads
          </TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients-audit">
            <UserCheck className="h-4 w-4 mr-1.5" /> Clients
          </TabsTrigger>
        </TabsList>
        <TabsContent value="leads" className="mt-4">
          <AuditTab entityType="lead" />
        </TabsContent>
        <TabsContent value="clients" className="mt-4">
          <AuditTab entityType="client" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function PlatformLeadAudit() {
  return (
    <PlatformLayout>
      <div className="p-3 sm:p-6">
        <LeadAuditContent />
      </div>
    </PlatformLayout>
  );
}
